import React, {useState, useRef, useEffect} from 'react';
import {Control, Controller} from 'react-hook-form';
import cn from 'classnames';
import {t} from '@lingui/core/macro';

import {extractTagBody, validateTagLabel} from 'shared/lib/validation/validateTagLabel';
import Alert from 'shared/ui/Alert/Alert';
import Edit from 'shared/ui/icons/General/Edit';

import styles from './ChipInput.module.scss';

interface ChipInputProps {
    name: string;
    control: Control<any>;
    availableOptions?: string[];
    disabled?: boolean;
}

const ChipInput: React.FC<ChipInputProps> = ({
    name,
    control,
    availableOptions = [],
    disabled = false,
}) => {
    const [inputValue, setInputValue] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [error, setError] = useState('');

    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    return (
        <Controller
            name={name}
            control={control}
            render={({field}) => {
                const chips = field.value || [];
                const hasValue = chips.length > 0 || inputValue.length > 0;
                const filteredOptions = (() => {
                    const normalizedInput = inputValue.toLowerCase().trim();
                    const allOptions = [...(availableOptions || []), ...chips]
                        .filter(option => typeof option === 'string')
                        .filter((item, index, array) => array.indexOf(item) === index);

                    return allOptions.filter(option => {
                        const normalizedOption = option.toLowerCase();
                        return normalizedOption.includes(normalizedInput) &&
                            normalizedOption !== normalizedInput;
                    });
                })();

                const addChip = (value: string) => {
                    const trimmedValue = value.trim().toLowerCase();

                    if (chips.length >= 10) {
                        setError(t`Максимум можно добавить 10 тегов`);
                        return;
                    }

                    const validationError = validateTagLabel(trimmedValue);

                    if (validationError !== true) {
                        setError(validationError);
                        return;
                    }

                    const tagBody = extractTagBody(trimmedValue);
                    const isDuplicate = chips.some((chip: string) =>
                        extractTagBody(chip).toLowerCase() === tagBody);

                    if (!isDuplicate) {
                        field.onChange([...chips, trimmedValue]);
                    }
                    setError('');
                    setInputValue('');
                    setIsDropdownOpen(false);
                    inputRef.current?.focus();
                };

                const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        if (inputValue.trim()) {
                            addChip(inputValue);
                        }
                    } else if (e.key === 'Backspace' && inputValue === '' && chips.length > 0) {
                        removeChip(chips.length - 1);
                    }
                };

                const removeChip = (index: number) => {
                    const newChips = chips.filter((_: string, i: number) => i !== index);
                    field.onChange(newChips);
                    setError('');
                    setIsDropdownOpen(false);
                };

                const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value.toLowerCase();
                    setError('');
                    setInputValue(value);
                    setIsDropdownOpen(value.length > 0);
                    setIsDropdownOpen(true);
                };

                const handleInputFocus = () => {
                    setIsDropdownOpen(true);
                };

                // eslint-disable-next-line react-hooks/rules-of-hooks
                useEffect(() => {
                    const handleClickOutside = (event: MouseEvent) => {
                        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                            setIsDropdownOpen(false);
                        }
                    };

                    document.addEventListener('mousedown', handleClickOutside);
                    return () => document.removeEventListener('mousedown', handleClickOutside);
                }, []);

                return (
                    <>
                        <div className={styles.chipInputContainer} ref={dropdownRef}>
                            <span className={styles.icon}>
                                <Edit width={16} height={16}/>
                            </span>
                            <div className={cn(
                                styles.chipsContainer,
                                hasValue ? styles.hasValue : '',
                                styles.colorsContainer,
                            )}>
                                {chips.map((chip: string, index: number) => (
                                    <div key={index} className={styles.chip}>
                                        <span className={styles.с}>{chip}</span>
                                        <button
                                            type="button"
                                            onClick={() => removeChip(index)}
                                            className={styles.chipRemove}
                                            disabled={disabled}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                                <div className={styles.inputWrapper}>
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        onKeyDown={handleKeyDown}
                                        value={inputValue}
                                        onChange={handleInputChange}
                                        onFocus={handleInputFocus}
                                        placeholder={!hasValue ? t`Теги для группировки` : ''}
                                        className={styles.chipInput}
                                        disabled={disabled}
                                    />
                                </div>
                                {isDropdownOpen && (
                                    <div className={styles.dropdown}>
                                        {inputValue.trim() && (
                                            <button
                                                type="button"
                                                onMouseDown={(e) => {
                                                    e.stopPropagation();
                                                    addChip(inputValue);
                                                }}
                                                className={cn(styles.dropdownItem, styles.addButton)}
                                            >
                                                {t`+ Добавить`} {inputValue}
                                            </button>
                                        )}

                                        {filteredOptions.map((option, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                onMouseDown={(e) => {
                                                    e.stopPropagation();
                                                    addChip(option);
                                                }}
                                                className={styles.dropdownItem}
                                            >
                                                {option}
                                            </button>
                                        ))}

                                        {filteredOptions.length === 0 && inputValue.trim() && (
                                            <div className={styles.dropdownItem}>
                                                {t`Ничего не найдено`}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {error && <Alert>{error}</Alert>}
                    </>
                );
            }}
        />
    );
};

export default ChipInput;